import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Modal, message } from 'antd';
import { useMutation } from '@apollo/client';
import { NOTIFY_WORKBENCH_DSP_BY_LARK } from '../graphql/mutations/workbenchMutations';

interface NotifyDspButtonProps {
  warehouseId?: number;
  alertId: number;
  /** 可不传：上游只按 alertId（taskId）就会给该任务下所有 DSP 发消息 */
  dspId?: number;
  dspName?: string | null;
  /** 按钮文案，缺省为「联系 DSP」 */
  label?: string;
  block?: boolean;
  /** 发送成功后回调：上游会把告警一并置为终态，调用方据此刷新列表 */
  onNotified?: () => void;
}

/**
 * 「联系 DSP」按钮：把告警发到该 DSP 的 Lark 群。
 *
 * 走二次确认弹窗而非单击直发 —— 这是一个**对外可见**的动作（消息真的会进 DSP 的群），
 * 误触无法撤回，且附言是一并发出去的内容，需要给仓管一个写入与复核的机会。
 */
const NotifyDspButton: React.FC<NotifyDspButtonProps> = ({
  warehouseId,
  alertId,
  dspId,
  dspName,
  label,
  block,
  onNotified,
}) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [notify, { loading }] = useMutation(NOTIFY_WORKBENCH_DSP_BY_LARK);

  const handleConfirm = async () => {
    if (!warehouseId) return;
    try {
      const { data } = await notify({
        variables: {
          warehouseId,
          alertId,
          dspId: dspId ?? null,
          comment: comment.trim() || null,
          language: normalizeLarkLanguage(i18n.language),
        },
      });
      // 上游返回 false 不是异常：可能该 DSP 未配置 Lark 群，或本次被幂等拦截。
      // 此时告警没被置为终态，故不触发刷新，卡片留在「实时」tab
      if (data?.notifyWorkbenchDspByLark) {
        message.success(String(t('workbench.alertText.notifyDspSent')));
        onNotified?.();
      } else {
        message.info(String(t('workbench.alertText.notifyDspNotSent')));
      }
      setOpen(false);
      setComment('');
    } catch (e) {
      message.error(String(t('workbench.alertText.notifyDspFailed')));
    }
  };

  return (
    <>
      <Button
        size="small"
        type="primary"
        block={block}
        disabled={!warehouseId}
        onClick={() => setOpen(true)}
      >
        {label ?? String(t('workbench.alertText.notifyDsp'))}
      </Button>

      <Modal
        open={open}
        title={String(
          dspName
            ? t('workbench.alertText.notifyDspTitleNamed', { dspName })
            : t('workbench.alertText.notifyDspTitle')
        )}
        okText={String(t('workbench.alertText.notifyDspConfirm'))}
        confirmLoading={loading}
        onOk={handleConfirm}
        onCancel={() => setOpen(false)}
        destroyOnClose
      >
        <p className="text-sm text-gray-600">
          {String(t('workbench.alertText.notifyDspHint'))}
        </p>
        <Input.TextArea
          className="mt-3"
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={String(t('workbench.alertText.notifyDspCommentPlaceholder'))}
        />
      </Modal>
    </>
  );
};

/** 归一 i18next 语言码为 basic 侧枚举（zh/en/es）；未知或空传 null，由上游按默认中文兜底 */
function normalizeLarkLanguage(raw: string | undefined): string | null {
  const primary = (raw ?? '').toLowerCase().split('-')[0];
  if (primary === 'zh' || primary === 'en' || primary === 'es') return primary;
  return null;
}

export default NotifyDspButton;
